import { FaArrowRight } from "react-icons/fa";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { IoIosSettings } from "react-icons/io";
import { Link } from "react-router-dom";
import React, { useContext, useState } from "react";
import { MdClose } from "react-icons/md";

import { StyledButton } from "../../components";
import useSocket from "../../hooks/useWebsocket";
import useQuizSlideInfo from "../../hooks/useQuizSlideInfo";
import { purple } from "../../config";
import useCurrentSlideData from "../../hooks/useCurrentSlideData";
import { SlideData } from "../../interfaces/question.interface";
import LeaveQuizButton from "../../components/LeaveQuizButton";
import { WebsocketContext } from "../../contexts/websocketContext";
import { LangContext } from "@natklok/contexts/langContext";
import useTranslatePageText from "@natklok/hooks/useTranslatePageText";

const HostQuizControlsWrapper: React.FC<{
  children: React.ReactElement;
  inLobby?: boolean;
}> = ({ children, inLobby = false }) => {
  const { roomCode } = useContext(WebsocketContext);
  const translatePageText = useTranslatePageText();
  return (
    <div>
      <div
        style={{
          color: "white",
        }}
      >
        <Box
          paddingX={4}
          paddingY={1}
          marginBottom={2}
          style={{
            background: purple[600],
            textAlign: "center",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <GameControls inLobby={inLobby} />
          <Typography variant="body2">
            <span style={{ opacity: 0.6 }}>{translatePageText("forTeachers.gamesCode")}</span>{" "}
            <b>{roomCode}</b>
          </Typography>
          <div>
            <ContinueButton inLobby={inLobby} />
          </div>
        </Box>
      </div>
      {children}
    </div>
  );
};

const GameControls: React.FC<{ inLobby: boolean }> = ({ inLobby }) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { translatePageText } = useContext(LangContext);

  if (isMobile) {
    return (
      <>
        <StyledButton
          onClick={() => {
            setMenuIsOpen(true);
          }}
          variant="secondary"
          label={menuIsOpen ? "" : translatePageText("general.menu")}
          icon={menuIsOpen ? <MdClose fontSize="1.4rem" style={{ marginRight: -6 }} /> : undefined}
          size="small"
          style={{
            margin: 0,
            padding: menuIsOpen ? 7 : "7px 13px",
          }}
        />
        {menuIsOpen && (
          <Box
            position="fixed"
            top={0}
            left={0}
            width="100%"
            height="100%"
            bgcolor="rgba(29, 28, 87, 0.95)"
            className="PauseScreen-root"
            zIndex={100}
            display="flex"
            justifyContent="center"
            alignItems="center"
            paddingTop={1}
          >
            <Box>
              <Box
                sx={{ width: [200, 340] }}
                mb={0.5}
                gap={1}
                display="flex"
                flexDirection="column"
              >
                <StyledButton size="small" onClick={() => setMenuIsOpen(false)}>
                  Tillbaka
                </StyledButton>
                <EditQuizButton inLobby={inLobby} />
                <LeaveQuizButton fullWidth size="medium" />
              </Box>
            </Box>
          </Box>
        )}
      </>
    );
  } else {
    return (
      <Box display="flex" flexDirection="row-reverse" gap={1}>
        <EditQuizButton inLobby={inLobby} />
        <LeaveQuizButton size="small" fullWidth style={{ minWidth: 200 }} />
      </Box>
    );
  }
};

const EditQuizButton: React.FC<{ inLobby: boolean }> = ({ inLobby }) => {
  const translatePageText = useTranslatePageText();
  if (!inLobby) return null;
  return (
    <Link to="/multiplayer/skapa" style={{ marginRight: 8 }}>
      <StyledButton
        fullWidth
        size="small"
        style={{ minWidth: 200 }}
        variant="secondary"
        icon={<IoIosSettings />}
      >
        {translatePageText("forTeachers.editTheQuiz")}
      </StyledButton>
    </Link>
  );
};

const ContinueButton: React.FC<{ inLobby: boolean }> = ({ inLobby }) => {
  const { wsQuestionCompleted, wsStartGame } = useSocket();
  const { currentSlideIndex, questionAmount, discussionAmount, amountSlidesUncompleted } =
    useQuizSlideInfo();
  const currentSlideData: SlideData = useCurrentSlideData();
  const translatePageText = useTranslatePageText();

  let buttonText = "Fortsätt";

  if (currentSlideData === undefined) return null;

  if (currentSlideData.discussion === undefined && currentSlideIndex > questionAmount - 1) {
    buttonText = translatePageText("forTeachers.continueToDiscussion");
  } else if (currentSlideData.question !== undefined) {
    buttonText =
      translatePageText("forTeachers.continueToQuestion") +
      ` ${currentSlideIndex + 1}/${questionAmount}`;
  } else if (currentSlideData.discussion !== undefined) {
    buttonText =
      translatePageText("forTeachers.continueToDiscussion") +
      ` ${currentSlideIndex - questionAmount}/${discussionAmount}`;
  }

  if (inLobby) {
    return (
      <StyledButton
        size="small"
        icon={<FaArrowRight />}
        iconPlacement="right"
        fullWidth
        style={{
          minWidth: "240px",
        }}
        onClick={() => wsStartGame()}
      >
        {translatePageText("forTeachers.startQuiz")}
      </StyledButton>
    );
  }

  return (
    <StyledButton
      hasCooldown
      size="small"
      icon={<FaArrowRight />}
      iconPlacement="right"
      fullWidth
      style={{ minWidth: 240 }}
      onClick={() => wsQuestionCompleted()}
    >
      {amountSlidesUncompleted === 1 ? "Se resultatet" : buttonText}
    </StyledButton>
  );
};

export default HostQuizControlsWrapper;
